import type { DbEntity } from "@nexus/types"
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"

interface DocumentCache {
  // Documents by ID
  documents: Map<string, DbEntity.Document>
  // List results by query key
  listResults: Map<string, { documents: DbEntity.Document[]; total: number; timestamp: number }>
  // Subfolders by parent ID
  subfolders: Map<string, DbEntity.Document[]>
}

interface DocumentCacheContextType {
  // Cache operations
  getCachedDocument: (id: string) => DbEntity.Document | undefined
  setCachedDocument: (document: DbEntity.Document) => void
  getCachedList: (key: string) => { documents: DbEntity.Document[]; total: number } | undefined
  setCachedList: (key: string, result: { documents: DbEntity.Document[]; total: number }) => void
  getCachedSubfolders: (parentId: string | null) => DbEntity.Document[] | undefined
  setCachedSubfolders: (parentId: string | null, folders: DbEntity.Document[]) => void

  // Cache management
  invalidateDocument: (id: string) => void
  invalidateList: (key: string) => void
  invalidateAll: () => void

  // Optimistic updates
  optimisticDelete: (id: string, listKey: string) => void
  optimisticUpdate: (id: string, updates: Partial<DbEntity.Document>) => void
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

const DocumentCacheContext = createContext<DocumentCacheContextType | null>(null)

export const useDocumentCache = () => {
  const context = useContext(DocumentCacheContext)
  if (!context) {
    throw new Error("useDocumentCache must be used within DocumentCacheProvider")
  }
  return context
}

export const DocumentCacheProvider = ({ children }: PropsWithChildren) => {
  const [cache, setCache] = useState<DocumentCache>({
    documents: new Map(),
    listResults: new Map(),
    subfolders: new Map()
  })

  const getCachedDocument = useCallback(
    (id: string) => {
      return cache.documents.get(id)
    },
    [cache.documents]
  )

  const setCachedDocument = useCallback((document: DbEntity.Document) => {
    setCache((prev) => ({
      ...prev,
      documents: new Map(prev.documents).set(document.id, document)
    }))
  }, [])

  const getCachedList = useCallback(
    (key: string) => {
      const cached = cache.listResults.get(key)
      if (!cached) return undefined

      // Check if cache is still valid
      if (Date.now() - cached.timestamp > CACHE_TTL) {
        return undefined
      }

      return { documents: cached.documents, total: cached.total }
    },
    [cache.listResults]
  )

  const setCachedList = useCallback(
    (key: string, result: { documents: DbEntity.Document[]; total: number }) => {
      setCache((prev) => {
        const newDocuments = new Map(prev.documents)
        const newListResults = new Map(prev.listResults)

        // Cache individual documents
        for (const doc of result.documents) {
          newDocuments.set(doc.id, doc)
        }

        // Cache list result
        newListResults.set(key, {
          ...result,
          timestamp: Date.now()
        })

        return {
          ...prev,
          documents: newDocuments,
          listResults: newListResults
        }
      })
    },
    []
  )

  const getCachedSubfolders = useCallback(
    (parentId: string | null) => {
      return cache.subfolders.get(parentId || "root")
    },
    [cache.subfolders]
  )

  const setCachedSubfolders = useCallback(
    (parentId: string | null, folders: DbEntity.Document[]) => {
      setCache((prev) => ({
        ...prev,
        subfolders: new Map(prev.subfolders).set(parentId || "root", folders)
      }))
    },
    []
  )

  const invalidateDocument = useCallback((id: string) => {
    setCache((prev) => {
      const newDocuments = new Map(prev.documents)
      newDocuments.delete(id)

      // Also invalidate list results that might contain this document
      const newListResults = new Map<
        string,
        { documents: DbEntity.Document[]; total: number; timestamp: number }
      >()
      for (const [key, result] of prev.listResults.entries()) {
        if (!result.documents.some((doc) => doc.id === id)) {
          newListResults.set(key, result)
        }
      }

      return {
        ...prev,
        documents: newDocuments,
        listResults: newListResults
      }
    })
  }, [])

  const invalidateList = useCallback((key: string) => {
    setCache((prev) => {
      const newListResults = new Map(prev.listResults)
      newListResults.delete(key)
      return { ...prev, listResults: newListResults }
    })
  }, [])

  const invalidateAll = useCallback(() => {
    setCache({
      documents: new Map(),
      listResults: new Map(),
      subfolders: new Map()
    })
  }, [])

  const optimisticDelete = useCallback((id: string, listKey: string) => {
    setCache((prev) => {
      const newDocuments = new Map(prev.documents)
      const newListResults = new Map(prev.listResults)

      // Remove from documents cache
      newDocuments.delete(id)

      // Update list results
      const listResult = prev.listResults.get(listKey)
      if (listResult) {
        newListResults.set(listKey, {
          ...listResult,
          documents: listResult.documents.filter((doc) => doc.id !== id),
          total: listResult.total - 1
        })
      }

      return {
        ...prev,
        documents: newDocuments,
        listResults: newListResults
      }
    })
  }, [])

  const optimisticUpdate = useCallback((id: string, updates: Partial<DbEntity.Document>) => {
    setCache((prev) => {
      const newDocuments = new Map(prev.documents)
      const newListResults = new Map(prev.listResults)

      // Update document in cache
      const existingDoc = prev.documents.get(id)
      if (existingDoc) {
        newDocuments.set(id, { ...existingDoc, ...updates })
      }

      // Update document in list results
      for (const [key, result] of prev.listResults.entries()) {
        const updatedDocuments = result.documents.map((doc) =>
          doc.id === id ? { ...doc, ...updates } : doc
        )
        newListResults.set(key, { ...result, documents: updatedDocuments })
      }

      return {
        ...prev,
        documents: newDocuments,
        listResults: newListResults
      }
    })
  }, [])

  const value = useMemo(
    () => ({
      getCachedDocument,
      setCachedDocument,
      getCachedList,
      setCachedList,
      getCachedSubfolders,
      setCachedSubfolders,
      invalidateDocument,
      invalidateList,
      invalidateAll,
      optimisticDelete,
      optimisticUpdate
    }),
    [
      getCachedDocument,
      setCachedDocument,
      getCachedList,
      setCachedList,
      getCachedSubfolders,
      setCachedSubfolders,
      invalidateDocument,
      invalidateList,
      invalidateAll,
      optimisticDelete,
      optimisticUpdate
    ]
  )

  return <DocumentCacheContext.Provider value={value}>{children}</DocumentCacheContext.Provider>
}
